import React from "react";

// Original, code-native item-type illustrations used ONLY as a visual fallback for
// products that have no photo. Generic shapes only — no brand marks, no exact-model
// detail, and never bundled accessories for a barebone CPU. These are decorative and
// are NEVER written into product data / JSON-LD / og:image.

const G = "#1f4a38";   // dark green strokes
const O = "#E8501A";   // orange accent
const Y = "#F4B400";   // yellow accent
const W = "#ffffff";

// Map a product to one illustration kind using type/subcategory first, then safe name hints.
export const classifyIllustration = (p) => {
  const t = `${p.subcategory || p.type || ""}`.toLowerCase();
  const n = `${p.name || ""}`.toLowerCase();
  const c = `${p.category || ""}`.toLowerCase();
  if (t.includes("laptop")) return "laptop";
  if (t.includes("allinone") || t.includes("all in one") || t.includes("all-in-one")) return "aio";
  if (t === "ops" || t.includes("interactive") || t.includes("panel")) return "panel";
  if (t.includes("tiny") || t.includes("mini")) return "mini";
  if (t.includes("monitor")) return "monitor";          // incl. Monitor/TV
  if (t.includes("tv")) return "tv";
  if (t.includes("printer")) return "printer";
  if (t.includes("server")) return "server";
  if (t.includes("ups")) return "ups";
  if (t.includes("ac") || t.includes("vrv") || t.includes("vrf")) return "ac";
  if (t.includes("graphic card") || n.includes("graphic card") || t === "gpu") return "gpu";
  if (t.includes("ram") || n.includes(" ram")) return "ram";
  if (t.includes("portable")) return "portable";
  if (t.includes("hdd") || t.includes("ssd") || t.includes("nvme") || t.includes("sata") || t.includes("sas") || t.includes("storage")) return "drive";
  if (t.includes("cable")) return "cable";
  if (t.includes("network") || t.includes("switch") || t.includes("firewall") || t.includes("router") || n.includes("meraki") || n.includes("fortigate") || n.includes("versa")) return "networking";
  if (t.includes("desktop")) return "desktop";          // incl. Desktop - Graphic
  if (c.includes("office")) return "office";
  return "office"; // neutral equipment for anything ambiguous — never guess a specific unit
};

const SHAPES = {
  laptop: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="28" y="16" width="64" height="40" rx="3" fill={W} />
      <rect x="40" y="26" width="40" height="20" rx="1.5" stroke={Y} strokeWidth="2.5" />
      <path d="M14 66 h92 l-9 -9 H23 Z" fill={W} />
    </g>
  ),
  aio: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="22" y="12" width="76" height="48" rx="3" fill={W} />
      <rect x="32" y="20" width="56" height="30" stroke={Y} strokeWidth="2.5" />
      <rect x="53" y="60" width="14" height="8" fill={W} />
      <path d="M40 72 h40" />
    </g>
  ),
  desktop: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="44" y="12" width="34" height="60" rx="3" fill={W} />
      <line x1="51" y1="22" x2="71" y2="22" strokeWidth="2.5" />
      <line x1="51" y1="29" x2="71" y2="29" strokeWidth="2.5" />
      <circle cx="61" cy="62" r="3.2" fill={O} stroke="none" />
    </g>
  ),
  mini: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="32" y="30" width="56" height="30" rx="5" fill={W} />
      <circle cx="43" cy="45" r="3.2" fill={O} stroke="none" />
      <line x1="55" y1="45" x2="79" y2="45" strokeWidth="2.5" />
    </g>
  ),
  monitor: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="20" y="14" width="80" height="46" rx="3" fill={W} />
      <rect x="30" y="22" width="60" height="30" stroke={Y} strokeWidth="2.5" />
      <rect x="54" y="60" width="12" height="8" fill={W} />
      <path d="M44 72 h32" />
    </g>
  ),
  tv: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="12" y="14" width="96" height="50" rx="3" fill={W} />
      <rect x="22" y="22" width="76" height="34" stroke={Y} strokeWidth="2.5" />
      <path d="M46 74 l14 -10 14 10" />
    </g>
  ),
  panel: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="14" y="12" width="92" height="54" rx="4" fill={W} />
      <circle cx="42" cy="39" r="4" fill={Y} stroke="none" />
      <circle cx="60" cy="39" r="4" fill={O} stroke="none" />
      <circle cx="78" cy="39" r="4" fill={Y} stroke="none" />
      <path d="M50 74 h20" />
    </g>
  ),
  printer: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="40" y="14" width="40" height="14" fill={W} />
      <rect x="28" y="28" width="64" height="30" rx="3" fill={W} />
      <rect x="42" y="50" width="36" height="14" fill={W} />
      <rect x="46" y="48" width="28" height="7" fill={Y} stroke="none" />
      <circle cx="84" cy="44" r="2.6" fill={O} stroke="none" />
    </g>
  ),
  server: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="30" y="16" width="60" height="52" rx="3" fill={W} />
      <line x1="38" y1="29" x2="74" y2="29" strokeWidth="2.5" />
      <line x1="38" y1="40" x2="74" y2="40" strokeWidth="2.5" />
      <line x1="38" y1="51" x2="74" y2="51" strokeWidth="2.5" />
      <circle cx="80" cy="29" r="2.4" fill={O} stroke="none" />
      <circle cx="80" cy="40" r="2.4" fill={Y} stroke="none" />
    </g>
  ),
  ups: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="40" y="14" width="40" height="56" rx="3" fill={W} />
      <rect x="48" y="24" width="24" height="6" fill={Y} stroke="none" />
      <rect x="48" y="34" width="24" height="6" fill={Y} stroke="none" />
      <path d="M62 48 h-6 l8 12 h-6 l6 10" stroke={O} strokeWidth="3" />
    </g>
  ),
  gpu: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="18" y="28" width="84" height="26" rx="2" fill={W} />
      <circle cx="42" cy="41" r="9" strokeWidth="3" />
      <circle cx="74" cy="41" r="9" strokeWidth="3" />
      <rect x="22" y="54" width="18" height="6" fill={Y} stroke="none" />
    </g>
  ),
  ram: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="20" y="30" width="80" height="24" rx="2" fill={W} />
      <rect x="30" y="36" width="12" height="10" strokeWidth="2.5" />
      <rect x="54" y="36" width="12" height="10" strokeWidth="2.5" />
      <rect x="78" y="36" width="12" height="10" strokeWidth="2.5" />
      <rect x="28" y="54" width="64" height="6" fill={Y} stroke="none" />
    </g>
  ),
  drive: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="30" y="20" width="60" height="44" rx="3" fill={W} />
      <circle cx="60" cy="42" r="12" strokeWidth="3" />
      <circle cx="60" cy="42" r="2.6" fill={O} stroke="none" />
      <rect x="36" y="25" width="10" height="5" fill={Y} stroke="none" />
    </g>
  ),
  portable: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="28" y="26" width="52" height="34" rx="7" fill={W} />
      <rect x="36" y="33" width="36" height="5" fill={Y} stroke="none" />
      <path d="M80 36 q13 0 13 13" strokeWidth="3" />
    </g>
  ),
  networking: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="16" y="30" width="88" height="24" rx="3" fill={W} />
      <rect x="26" y="44" width="7" height="6" fill={G} stroke="none" />
      <rect x="38" y="44" width="7" height="6" fill={G} stroke="none" />
      <rect x="50" y="44" width="7" height="6" fill={G} stroke="none" />
      <rect x="62" y="44" width="7" height="6" fill={G} stroke="none" />
      <circle cx="94" cy="37" r="2.4" fill={O} stroke="none" />
      <circle cx="86" cy="37" r="2.4" fill={Y} stroke="none" />
    </g>
  ),
  cable: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <path d="M26 32 q22 -16 44 0 t42 2" strokeWidth="4" />
      <rect x="20" y="44" width="13" height="18" rx="2" fill={W} />
      <rect x="86" y="44" width="13" height="18" rx="2" fill={O} stroke="none" />
    </g>
  ),
  ac: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="20" y="20" width="80" height="48" rx="4" fill={W} />
      <circle cx="56" cy="44" r="15" strokeWidth="3" />
      <path d="M56 44 q10 -8 13 3 M56 44 q-2 13 -12 7 M56 44 q-8 -5 -1 -10" strokeWidth="2.5" />
      <circle cx="56" cy="44" r="3" fill={O} stroke="none" />
      <line x1="84" y1="34" x2="92" y2="34" strokeWidth="2.5" />
      <line x1="84" y1="44" x2="92" y2="44" strokeWidth="2.5" />
      <line x1="84" y1="54" x2="92" y2="54" strokeWidth="2.5" />
    </g>
  ),
  office: (
    <g fill="none" stroke={G} strokeWidth="3.5" strokeLinejoin="round">
      <rect x="28" y="24" width="64" height="42" rx="3" fill={W} />
      <line x1="28" y1="39" x2="92" y2="39" strokeWidth="2.5" />
      <rect x="52" y="29" width="16" height="6" rx="1" fill={Y} stroke="none" />
      <line x1="60" y1="47" x2="60" y2="58" strokeWidth="2.5" />
    </g>
  ),
};

export const ProductIllustration = ({ p, testid }) => {
  const kind = classifyIllustration(p);
  return (
    <div
      data-testid={testid || `product-illustration-${p.id}`}
      data-illustration-kind={kind}
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden"
      style={{ background: "#f6f3ea" }}
    >
      <div className="flex w-full flex-1 items-center justify-center px-3 pt-3">
        <svg viewBox="0 0 120 84" aria-hidden="true" focusable="false"
             className="w-[56%] max-w-[150px]" style={{ height: "auto" }}>
          {SHAPES[kind] || SHAPES.office}
        </svg>
      </div>
      <div className="w-full px-2 pb-1.5 text-center">
        <span className="inline-block whitespace-normal break-words text-[10px] font-semibold leading-snug"
              style={{ color: "var(--green-900)" }}>
          Illustrative image — actual unit photos pending
        </span>
      </div>
    </div>
  );
};
